import { Actions, Async, Dispatchers } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ExtrasModel } from '../model/extras.model';
import { ContentLink } from '../model/extras.domain';
import {
  defaultValues,
  toCreateContentInput,
  toMixpanelContentType,
  toUpdateContentInput,
} from '../viewModel/extras.mappers';
import { toErrors } from '@client/new/pages/promotions/view-model/newDiscount.mappers';
import { mixTrack } from '@services/mixpanel';
import toast from 'react-hot-toast';
import { useKeepScrollPosition } from '@client/new/hooks/useKeepScrollPosition';
import { createLinkInputSchema, updateLinkInputSchema } from './link.schema';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import {
  createFormRegister,
  createOnChangeHandler,
  ExtendedFormRegister,
  getFormMode,
} from '../viewModel/formHelpers';

type LinkActionMap = {
  PrepareCreate: { parentId: string };
  Edit: ContentLink['value'];
  Cancel: null;
};

export type LinkVMAction = Actions<LinkActionMap>;

type LinkMode = 'none' | 'create' | 'edit';

type LinkState = {
  parentId: string;
  mode: LinkMode;
  content: ContentLink['value'];
};

type LinkViewModelParams = {
  model: ExtrasModel;
};

export type LinkViewModel = LinkState &
  Dispatchers<LinkActionMap> & {
    register: ExtendedFormRegister<ContentLink['value']>;
    errors: any;
    values: ContentLink['value'];
    onChange: (name: keyof ContentLink['value'], value: any) => void;
    handleCreate: () => Promise<void>;
  };

export const useLinkViewModel = (
  p: LinkViewModelParams,
): Async<LinkViewModel> => {
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const [state, setState] = useState<LinkState>({
    parentId: '',
    mode: 'none',
    content: { ...defaultValues['ContentLink'] },
  });

  useKeepScrollPosition(state.mode, 'none');

  const form = useForm<ContentLink['value']>({
    reValidateMode: 'onBlur',
    defaultValues: defaultValues['ContentLink'],
    resolver: zodResolver(
      state.mode === 'create' ? createLinkInputSchema : updateLinkInputSchema,
    ),
  });

  useEffect(() => {
    if (state.content && Object.keys(state.content).length > 0) {
      form.reset({ ...state.content });
    }
  }, [form, state.content, state.parentId]);

  const values = form.watch();

  const onSubmit = (value: ContentLink['value']) => {
    const mergedValue = { ...state.content, ...value };
    p.model
      .update(
        state.content.id,
        toUpdateContentInput({ type: 'ContentLink', value: mergedValue }),
      )
      .then(() => {
        setState((prev) => ({
          ...prev,
          content: mergedValue,
        }));

        mixTrack({
          type: 'Extras Block Saved',
          payload: {
            title: value.title,
            type: toMixpanelContentType('ContentLink'),
            'video time': 0,
            'video aspect ratio': 'n/a',
            'Comments on': false,
          },
        });
      })
      .catch((error) => {
        console.error('Error updating Link:', error);
        toast.error('Failed to update Link content');
      });
  };

  const onError = () => {
    setShowErrors(true);
  };

  const register = createFormRegister({
    form,
    onSubmit,
    onError,
    showErrors,
    mode: getFormMode(state.mode),
  });

  const onChange = createOnChangeHandler({
    form,
    onSubmit,
    onError,
    mode: getFormMode(state.mode),
  });

  const handleCreate = async () => {
    // Prepare data for validation with required fields for create mode
    const formData = form.getValues();
    const dataToValidate = {
      ...formData,
      coverImage: values.coverImage || '', // Include coverImage from watched values
      id: uuid(), // Generate UUID for new content
      parentId: state.parentId, // Required for create
      isPublished: true, // Default to published
    };

    const validationResult = createLinkInputSchema.safeParse(dataToValidate);

    if (!validationResult.success) {
      // On validation error, show errors
      setShowErrors(true);
      // Update form errors manually - TypeScript doesn't recognize discriminated union in else block
      const zodError = validationResult as unknown as { error: z.ZodError };
      zodError.error.errors.forEach((error) => {
        if (error.path.length > 0) {
          const fieldName = error.path[0] as keyof ContentLink['value'];
          form.setError(fieldName, {
            type: 'manual',
            message: error.message,
          });
        }
      });
      return;
    }

    // Validation succeeded
    try {
      // Extract only domain fields for ContentLink
      const { title, url, coverImage, id } = validationResult.data;

      // Create link through model
      await p.model.create({
        input: toCreateContentInput(
          {
            type: 'ContentLink',
            value: {
              id,
              title,
              url,
              coverImage: coverImage || '',
              purchaseInfo: { type: 'not-purchasable' },
            },
          },
          state.parentId,
        ),
      });

      // Reset form and close modal on success
      setState((prev) => ({
        ...prev,
        mode: 'none',
        content: { ...defaultValues['ContentLink'] },
      }));
      setShowErrors(false);

      mixTrack({
        type: 'Extras Block Saved',
        payload: {
          title: validationResult.data.title,
          type: toMixpanelContentType('ContentLink'),
          'video time': 0,
          'video aspect ratio': 'n/a',
          'Comments on': false,
        },
      });
    } catch (error) {
      console.error('Error creating Link:', error);
      toast.error('Failed to create Link');
    }
  };

  const dispatchers = getDispatchers<LinkActionMap>(
    _dispatch(setState, p.model),
  );

  return {
    type: 'fetched',
    value: {
      ...state,
      ...dispatchers,
      register,
      onChange,
      values,
      errors: showErrors ? toErrors(form.formState.errors) : null,
      handleCreate,
    },
  };
};

const _dispatch =
  (
    setState: React.Dispatch<React.SetStateAction<LinkState>>,
    model: ExtrasModel,
  ) =>
  (a: LinkVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'PrepareCreate':
        return setState((prev) => ({
          ...prev,
          parentId: a.payload.parentId,
          mode: 'create',
          content: { ...defaultValues['ContentLink'] },
        }));

      case 'Edit':
        return setState((prev) => ({
          ...prev,
          content: a.payload,
          mode: 'edit',
        }));

      case 'Cancel':
        return setState(onCancel);
    }
  };

const onCancel = (prev: LinkState): LinkState => {
  return {
    ...prev,
    mode: 'none',
    content: { ...defaultValues['ContentLink'] },
  };
};
